import { useState } from 'react'

interface QueryTerm {
  id: number
  boolOp: string
  term: string
  field: string
  openParen: boolean
  closeParen: boolean
}

const FIELDS = [
  { value: '[all]', label: 'All Fields' },
  { value: '[tiab]', label: 'Title/Abstract' },
  { value: '[ti]', label: 'Title' },
  { value: '[tw]', label: 'Text Words' },
  { value: '[au]', label: 'Author' },
  { value: '[ta]', label: 'Journal' },
  { value: '[mesh]', label: 'MeSH Terms' },
  { value: '[majr]', label: 'MeSH Major Topic' },
  { value: '[sh]', label: 'MeSH Subheading' },
  { value: '[dp]', label: 'Publication Date' },
  { value: '[pt]', label: 'Publication Type' },
  { value: '[pmid]', label: 'PubMed ID' },
  { value: '[ad]', label: 'Affiliation' },
]

const PUB_TYPES = [
  { value: '', label: 'Any' },
  { value: 'review[pt]', label: 'Review' },
  { value: 'clinical trial[pt]', label: 'Clinical Trial' },
  { value: 'meta-analysis[pt]', label: 'Meta-Analysis' },
  { value: 'randomized controlled trial[pt]', label: 'Randomized Controlled Trial' },
]

interface QueryBuilderProps {
  onQueryBuilt: (query: string) => void
}

export default function QueryBuilder({ onQueryBuilt }: QueryBuilderProps) {
  const [terms, setTerms] = useState<QueryTerm[]>([
    { id: 1, boolOp: '', term: '', field: '[tiab]', openParen: false, closeParen: false }
  ])
  const [yearFrom, setYearFrom] = useState<number | ''>('')
  const [yearTo, setYearTo] = useState<number | ''>('')
  const [pubType, setPubType] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)

  const addTerm = () => {
    const newId = Math.max(...terms.map(t => t.id), 0) + 1
    setTerms([...terms, { id: newId, boolOp: 'AND', term: '', field: '[tiab]', openParen: false, closeParen: false }])
  }

  const removeTerm = (id: number) => {
    if (terms.length <= 1) return
    setTerms(terms.filter(t => t.id !== id))
  }

  const updateTerm = (id: number, field: keyof QueryTerm, value: any) => {
    setTerms(terms.map(t => t.id === id ? { ...t, [field]: value } : t))
  }

  const buildQuery = () => {
    const parts: string[] = []
    
    terms.forEach((term, index) => {
      if (!term.term.trim()) return
      
      let part = ''
      
      // Boolean operator (not for first term)
      if (index > 0 && term.boolOp) {
        part += term.boolOp + ' '
      }
      
      // Open parenthesis
      if (term.openParen) {
        part += '('
      }
      
      // Term with field
      if (term.field === '[all]') {
        part += term.term
      } else {
        part += term.term + term.field
      }
      
      // Close parenthesis
      if (term.closeParen) {
        part += ')'
      }
      
      if (part) {
        parts.push(part)
      }
    })
    
    // Year filter
    if (yearFrom && yearTo) {
      if (yearFrom === yearTo) {
        parts.push((parts.length > 0 ? 'AND ' : '') + `${yearFrom}[dp]`)
      } else {
        parts.push((parts.length > 0 ? 'AND ' : '') + `${yearFrom}:${yearTo}[dp]`)
      }
    } else if (yearFrom) {
      parts.push((parts.length > 0 ? 'AND ' : '') + `${yearFrom}:3000[dp]`)
    } else if (yearTo) {
      parts.push((parts.length > 0 ? 'AND ' : '') + `1900:${yearTo}[dp]`)
    }
    
    // Publication type
    if (pubType) {
      parts.push((parts.length > 0 ? 'AND ' : '') + pubType)
    }
    
    return parts.join(' ')
  }

  const handleBuild = () => {
    const query = buildQuery()
    if (query) {
      onQueryBuilt(query)
    }
  }

  const handleClear = () => {
    setTerms([{ id: 1, boolOp: '', term: '', field: '[tiab]', openParen: false, closeParen: false }])
    setYearFrom('')
    setYearTo('')
    setPubType('')
  }

  return (
    <div className="border rounded-lg mb-4">
      <div 
        className="bg-gray-50 px-4 py-3 cursor-pointer flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          <span className="font-medium">Query Builder</span>
          <span className="text-sm text-gray-500">(click to expand)</span>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isExpanded && (
        <div className="p-4 space-y-4">
          {terms.map((term, index) => (
            <div key={term.id} className="space-y-2 p-3 bg-gray-50 rounded-md">
              {index > 0 && (
                <select
                  value={term.boolOp}
                  onChange={(e) => updateTerm(term.id, 'boolOp', e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                  <option value="NOT">NOT</option>
                </select>
              )}
              
              <div className="flex gap-2 items-center">
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={term.openParen}
                    onChange={(e) => updateTerm(term.id, 'openParen', e.target.checked)}
                    className="rounded"
                  />
                  (
                </label>
                
                <input
                  type="text"
                  value={term.term}
                  onChange={(e) => updateTerm(term.id, 'term', e.target.value)}
                  placeholder="Enter search term..."
                  className="flex-1 px-3 py-1 border rounded text-sm"
                />
                
                <select
                  value={term.field}
                  onChange={(e) => updateTerm(term.id, 'field', e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  {FIELDS.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={term.closeParen}
                    onChange={(e) => updateTerm(term.id, 'closeParen', e.target.checked)}
                    className="rounded"
                  />
                  )
                </label>
                
                {terms.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTerm(term.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addTerm}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Term
          </button>
          
          <hr className="border-gray-200" />
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Year From</label>
              <input
                type="number"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value ? Number(e.target.value) : '')}
                placeholder="1900"
                className="w-full px-3 py-1 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Year To</label>
              <input
                type="number"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value ? Number(e.target.value) : '')}
                placeholder="2024"
                className="w-full px-3 py-1 border rounded text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Publication Type</label>
            <select
              value={pubType}
              onChange={(e) => setPubType(e.target.value)}
              className="w-full px-3 py-1 border rounded text-sm"
            >
              {PUB_TYPES.map(pt => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleBuild}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              Build Query
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
